import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './AdminPage.css';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001';

function AdminPage() {
  const [features, setFeatures] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('features');
  const [isReorganizing, setIsReorganizing] = useState(false);
  const [reorganizeMessage, setReorganizeMessage] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [show2FALogin, setShow2FALogin] = useState(false);
  const [twoFactorSecret, setTwoFactorSecret] = useState('');
  const [twoFactorQRCode, setTwoFactorQRCode] = useState('');
  const [twoFactorToken, setTwoFactorToken] = useState('');
  const [backupStatus, setBackupStatus] = useState('');
  const [backups, setBackups] = useState([]);
  const [isLoadingBackups, setIsLoadingBackups] = useState(false);
  const [restoreStatus, setRestoreStatus] = useState('');
  const [importStatus, setImportStatus] = useState('');
  const [featuresFile, setFeaturesFile] = useState(null);
  const [submissionsFile, setSubmissionsFile] = useState(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [editingBackup, setEditingBackup] = useState(null);
  const [editLabel, setEditLabel] = useState('');

  // Login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  useEffect(() => {
    // Force cache busting on page load
    const timestamp = Date.now();
    console.log(`AdminPage loaded at ${timestamp} - preventing cache`);
    
    // Check if user is already authenticated
    const token = localStorage.getItem('adminToken');
    if (token) {
      // Verify token is still valid
      verifyToken(token);
    } else {
      // No token, show login form
      setIsAuthenticated(false);
      setUser(null);
    }
    

  }, []);

  const verifyToken = async (token) => {
    try {
      const cacheBuster = `?_cb=${Date.now()}`;
      const response = await fetch(`${API_BASE}/api/admin/features${cacheBuster}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      
      if (response.ok) {
        const userData = JSON.parse(localStorage.getItem('adminUser'));
        
        // Validate that userData exists and has required fields
        if (!userData || !userData.username) {
          console.log('Invalid user data in localStorage, clearing and requiring re-login');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          setIsAuthenticated(false);
          return;
        }
        
        setIsAuthenticated(true);
        setUser(userData);
        
        // Check if user needs to complete setup
        if (userData.firstLogin) {
          // User needs to change password first
          setSetupComplete(false);
          setShowPasswordChange(true);
        } else if (!userData.twoFactorEnabled) {
          // User has changed password but hasn't completed 2FA setup yet
          setSetupComplete(false);
          setShow2FASetup(true);
        } else {
          // User has completed full setup
          setSetupComplete(true);
          loadFeatures();
          loadSubmissions();
          loadBackups();
        }
      } else {
        // Token is invalid, remove it
        console.log('Token verification failed, clearing localStorage');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');

    try {
      const cacheBuster = `?_cb=${Date.now()}`;
      const response = await fetch(`${API_BASE}/api/auth/login${cacheBuster}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success) {
        // Check if user has 2FA enabled
        if (data.user.twoFactorEnabled) {
          // User has 2FA enabled, show 2FA login form
          setShow2FALogin(true);
          setShowPasswordChange(false);
        } else {
          // No 2FA, proceed with normal login
          localStorage.setItem('adminToken', data.token);
          localStorage.setItem('adminUser', JSON.stringify(data.user));
          setIsAuthenticated(true);
          setUser(data.user);
          
          // If first login, show password change form
          if (data.user.firstLogin) {
            setShowPasswordChange(true);
          }
          
          loadFeatures();
          loadSubmissions();
          loadBackups();
        }
      } else {
        setLoginError(data.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setLoginError('Network error - please try again');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setIsAuthenticated(false);
    setUser(null);
    setFeatures({});
    setSubmissions([]);
    setShowPasswordChange(false);
    setShow2FASetup(false);
    setShow2FALogin(false);
  };

  const handle2FALogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');

    try {
      const cacheBuster = `?_cb=${Date.now()}`;
      const response = await fetch(`${API_BASE}/api/auth/login-with-2fa${cacheBuster}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
        body: JSON.stringify({ 
          username, 
          password, 
          token: twoFactorToken 
        })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', JSON.stringify(data.user));
        setIsAuthenticated(true);
        setUser(data.user);
        setShow2FALogin(false);
        setTwoFactorToken('');
        setSetupComplete(true);
        
        loadFeatures();
        loadSubmissions();
      } else {
        setLoginError(data.error || '2FA verification failed');
      }
    } catch (error) {
      console.error('2FA login error:', error);
      setLoginError('Network error - please try again');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const loadFeatures = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/features`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeatures(data);
      } else {
        console.error('Failed to load features');
      }
    } catch (error) {
      console.error('Error loading features:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSubmissions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/submissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSubmissions(data);
      }
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  };

  const deleteFeature = async (featureId) => {
    if (!window.confirm('Are you sure you want to delete this feature and all its sub-features?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/features/${featureId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await loadFeatures();
      }
    } catch (error) {
      console.error('Error deleting feature:', error);
    }
  };

  const deleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/submissions/${submissionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await loadSubmissions();
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
    }
  };

  const triggerReorganization = async () => {
    if (!window.confirm('This will trigger an AI agent to reorganize all features. This may take a few minutes. Continue?')) return;

    setIsReorganizing(true);
    setReorganizeMessage('');

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/reorganize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setReorganizeMessage(result.message);
        await loadFeatures();
      } else {
        setReorganizeMessage('Error: ' + (result.error || 'Reorganization failed'));
      }
    } catch (error) {
      console.error('Error triggering reorganization:', error);
      setReorganizeMessage('Error: Failed to trigger reorganization');
    } finally {
      setIsReorganizing(false);
    }
  };

  // Helper to trigger file download
  const downloadBackupFile = (type, timestamp) => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      alert('Missing admin token – please log in again.');
      return;
    }
    const url = `${API_BASE}/api/admin/download-backup?type=${type}&timestamp=${encodeURIComponent(timestamp)}&auth=${token}`;
    // Open in new tab/window so browser handles the file download UI natively
    window.open(url, '_blank');
  };

  const createBackup = async () => {
    try {
      setBackupStatus('Creating backup...');
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/backup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();
      if (result.success) {
        setBackupStatus(`Backup created successfully: ${result.submissions} submissions, ${result.features} features`);
        setTimeout(() => setBackupStatus(''), 5000);
        // Trigger downloads using timestamp returned by API (more reliable in production)
        if (result.timestamp) {
          downloadBackupFile('features', result.timestamp);
          downloadBackupFile('submissions', result.timestamp);
          downloadBackupFile('comments', result.timestamp);
                  // Optimistically add this backup to list for immediate UI feedback
        setBackups(prev => [{
          timestamp: result.timestamp,
          readableDate: new Date().toLocaleString(),
          features: `features-${result.timestamp}.csv`,
          submissions: `submissions-${result.timestamp}.csv`,
          comments: `comments-${result.timestamp}.csv`,
          hasFeatures: true,
          hasSubmissions: true,
          hasComments: true,
          label: '',
          type: 'manual'
        }, ...prev]);
        }
      } else {
        setBackupStatus('Error: ' + (result.error || 'Backup failed'));
      }
    } catch (error) {
      console.error('Error creating backup:', error);
      setBackupStatus('Error: Failed to create backup');
    }
  };

  const loadBackups = async () => {
    try {
      setIsLoadingBackups(true);
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/backups`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const result = await response.json();
      
      if (result.backups) {
        setBackups(result.backups);
        return result.backups;
      }
      return [];
    } catch (error) {
      console.error('Error loading backups:', error);
      return [];
    } finally {
      setIsLoadingBackups(false);
    }
  };

  const restoreFromBackup = async (timestamp) => {
    if (!window.confirm('Are you sure you want to restore from this backup? This will overwrite all current data and cannot be undone.')) {
      return;
    }

    try {
      setRestoreStatus('Restoring from backup...');
      const token = localStorage.getItem('adminToken');
      
      const response = await fetch(`${API_BASE}/api/admin/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ timestamp })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setRestoreStatus(`Restore completed successfully: ${result.features} features, ${result.submissions} submissions restored`);
        setTimeout(() => setRestoreStatus(''), 5000);
        // Refresh data after restore
        loadFeatures();
        loadSubmissions();
      } else {
        setRestoreStatus('Error: ' + (result.error || 'Restore failed'));
      }
    } catch (error) {
      console.error('Error restoring from backup:', error);
      setRestoreStatus('Error: Failed to restore from backup');
    }
  };

  const importBackupFiles = async (featuresFile, submissionsFile) => {
    if (!featuresFile || !submissionsFile) {
      setImportStatus('Error: Please select both features and submissions files');
      return;
    }

    if (!window.confirm('Are you sure you want to import this backup? This will overwrite all current data and cannot be undone.')) {
      return;
    }

    try {
      setImportStatus('Importing backup files...');
      
      // Read file contents
      const featuresData = await featuresFile.text();
      const submissionsData = await submissionsFile.text();
      
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/import-backup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ featuresData, submissionsData })
      });
      
      const result = await response.json();
      
      if (result.success) {
        setImportStatus(`Import completed successfully: ${result.features} features, ${result.submissions} submissions imported`);
        setTimeout(() => setImportStatus(''), 5000);
        // Refresh data after import
        loadFeatures();
        loadSubmissions();
      } else {
        setImportStatus('Error: ' + (result.error || 'Import failed'));
      }
    } catch (error) {
      console.error('Error importing backup:', error);
      setImportStatus('Error: Failed to import backup');
    }
  };

  const updateBackupLabel = async (timestamp, newLabel) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/backup/${timestamp}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ label: newLabel })
      });
      
      const result = await response.json();
      if (result.success) {
        // Update local state
        setBackups(prev => prev.map(backup => 
          backup.timestamp === timestamp 
            ? { ...backup, label: newLabel }
            : backup
        ));
        setEditingBackup(null);
        setEditLabel('');
      } else {
        alert('Failed to update backup label: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating backup label:', error);
      alert('Failed to update backup label');
    }
  };

  const startEditingLabel = (backup) => {
    setEditingBackup(backup.timestamp);
    setEditLabel(backup.label || '');
  };

  const cancelEditingLabel = () => {
    setEditingBackup(null);
    setEditLabel('');
  };

  const saveLabel = (timestamp) => {
    updateBackupLabel(timestamp, editLabel);
  };

  const totalReset = async () => {
    if (!window.confirm('This will DELETE ALL features and submissions permanently. Are you absolutely sure?')) return;
    
    const confirmation = window.prompt('This action cannot be undone. Type "RESET" to confirm:');
    if (confirmation !== 'RESET') {
      alert('Reset cancelled. You must type "RESET" exactly to confirm.');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        alert('Database reset successfully - all data has been deleted');
        setFeatures({});
        setSubmissions([]);
        await loadFeatures();
        await loadSubmissions();
      } else {
        alert('Error: ' + (result.error || 'Reset failed'));
      }
    } catch (error) {
      console.error('Error resetting database:', error);
      alert('Error: Failed to reset database');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    const currentPassword = document.getElementById('currentPassword').value;
    const newPassword = document.getElementById('newPassword').value;
    const confirmPassword = document.getElementById('confirmPassword').value;

    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      alert('New password must be at least 8 characters long');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      const result = await response.json();
      
      if (result.success) {
        if (result.requires2FASetup) {
          // Password changed, now setup 2FA
          alert('Password changed successfully! Now you must setup 2FA to continue.');
          setShowPasswordChange(false);
          setShow2FASetup(true);
          // Update user state - keep setupComplete false until 2FA is done
          const updatedUser = { ...user, firstLogin: false };
          setUser(updatedUser);
          localStorage.setItem('adminUser', JSON.stringify(updatedUser));
          // Automatically trigger 2FA setup
          await setup2FA();
        } else {
          alert('Password changed successfully!');
          setShowPasswordChange(false);
          setUser(prev => ({ ...prev, firstLogin: false }));
          localStorage.setItem('adminUser', JSON.stringify({ ...user, firstLogin: false }));
        }
      } else {
        alert('Error: ' + (result.error || 'Password change failed'));
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Error: Failed to change password');
    }
  };

  const setup2FA = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/auth/setup-2fa`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      
      if (result.success) {
        setTwoFactorSecret(result.secret);
        setTwoFactorQRCode(result.qrCode);
        setShow2FASetup(true);
      } else {
        alert('Error: ' + (result.error || '2FA setup failed'));
      }
    } catch (error) {
      console.error('Error setting up 2FA:', error);
      alert('Error: Failed to setup 2FA');
    }
  };

  const verify2FA = async () => {
    if (!twoFactorToken) {
      alert('Please enter the 2FA token');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/auth/verify-2fa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ token: twoFactorToken })
      });

      const result = await response.json();
      
      if (result.success) {
        alert('2FA enabled successfully! You will now be logged out to complete the setup. Please login again with your new password and 2FA.');
        setShow2FASetup(false);
        setTwoFactorToken('');
        
        // Logout user and clear all data
        handleLogout();
        
        // Show message about new login requirements
        alert('Setup complete! Please login again with your new password and 2FA token.');
      } else {
        alert('Error: ' + (result.error || '2FA verification failed'));
      }
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      alert('Error: Failed to verify 2FA');
    }
  };

  const renderFeatureHierarchy = (categoryFeatures) => {
    // Group features by level and parent
    const featureMap = new Map();
    const rootFeatures = [];

    categoryFeatures.forEach(feature => {
      featureMap.set(feature.id, { ...feature, children: [] });
    });

    categoryFeatures.forEach(feature => {
      if (feature.parent_id) {
        const parent = featureMap.get(feature.parent_id);
        if (parent) {
          parent.children.push(featureMap.get(feature.id));
        } else {
          // Parent not found in this category, treat as root
          rootFeatures.push(featureMap.get(feature.id));
        }
      } else {
        rootFeatures.push(featureMap.get(feature.id));
      }
    });

    // Also include any features that weren't added to the hierarchy
    categoryFeatures.forEach(feature => {
      const featureObj = featureMap.get(feature.id);
      if (!rootFeatures.includes(featureObj) && !categoryFeatures.some(f => f.parent_id === feature.id)) {
        // This feature isn't a root and isn't a child of anything, add it as root
        if (!rootFeatures.find(root => root.id === feature.id)) {
          rootFeatures.push(featureObj);
        }
      }
    });

    const renderFeature = (feature, level = 0) => (
      <div key={feature.id} className={`feature-item level-${level}`}>
        <div className="feature-content">
          <div className="feature-info">
            <span className="feature-title">{feature.title}</span>
            <span className="feature-meta">
              Level {feature.level} • {feature.votes || 0} votes
            </span>
          </div>
          <button 
            onClick={() => deleteFeature(feature.id)}
            className="delete-btn"
            title="Delete feature"
          >
            ×
          </button>
        </div>
        {feature.children.length > 0 && (
          <div className="feature-children">
            {feature.children.map(child => renderFeature(child, level + 1))}
          </div>
        )}
      </div>
    );

    return rootFeatures.map(feature => renderFeature(feature));
  };

  // Show 2FA login form if requested (must be checked BEFORE the generic login form)
  if (show2FALogin) {
    return (
      <div className="admin-page">
        <div className="twofa-login-container">
          <h2>Two-Factor Authentication Required</h2>
          <p>Please enter the 6-digit code from your authenticator app:</p>
          
          <form onSubmit={handle2FALogin} className="twofa-login-form">
            <input
              type="text"
              value={twoFactorToken}
              onChange={(e) => setTwoFactorToken(e.target.value)}
              placeholder="6-digit code"
              maxLength="6"
              required
            />
            
            <button type="submit" disabled={isLoggingIn}>
              {isLoggingIn ? 'Verifying...' : 'Verify & Login'}
            </button>
            
            <button type="button" onClick={() => setShow2FALogin(false)} className="secondary">
              Back to Login
            </button>
          </form>
          
          {loginError && <div className="error-message">{loginError}</div>}
        </div>
      </div>
    );
  }

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-page">
        <div className="login-container">
          <form onSubmit={handleLogin} className="login-form">
            <h2>Admin Login</h2>
            {loginError && <div className="error-message">{loginError}</div>}
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Username"
              required
            />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
            />
            <button type="submit" disabled={isLoggingIn}>
              {isLoggingIn ? 'Logging in...' : 'Login'}
            </button>
            <div className="login-help">
              <p>Default credentials: admin / admin123</p>
              <p>Please change the password on first login!</p>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Show password change form if first login
  if (showPasswordChange) {
    return (
      <div className="admin-page">
        <div className="password-change-container">
          <form onSubmit={handlePasswordChange} className="password-change-form">
            <h2>Change Default Password</h2>
            <p>This is your first login. Please change your password to continue.</p>
            
            <input
              type="password"
              id="currentPassword"
              placeholder="Current Password (admin123)"
              required
            />
            <input
              type="password"
              id="newPassword"
              placeholder="New Password (min 8 characters)"
              required
            />
            <input
              type="password"
              id="confirmPassword"
              placeholder="Confirm New Password"
              required
            />
            
            <button type="submit">Change Password</button>
          </form>
        </div>
      </div>
    );
  }

  // Show 2FA setup if requested
  if (show2FASetup) {
    return (
      <div className="admin-page">
        <div className="twofa-setup-container">
          <h2>Setup Two-Factor Authentication</h2>
          <p>Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.):</p>
          
          {twoFactorQRCode && (
            <div className="qr-code-container">
              <img src={twoFactorQRCode} alt="2FA QR Code" />
              <p>Secret: {twoFactorSecret}</p>
            </div>
          )}
          
          <p>Enter the 6-digit code from your app to verify:</p>
          <input
            type="text"
            value={twoFactorToken}
            onChange={(e) => setTwoFactorToken(e.target.value)}
            placeholder="6-digit code"
            maxLength="6"
          />
          
          <div className="twofa-actions">
            <button onClick={verify2FA}>Verify & Enable 2FA</button>
            {!twoFactorQRCode && (
              <button onClick={setup2FA} className="secondary">Generate QR Code</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // (moved 2FA login block above the generic login block)

  if (isLoading) {
    return (
      <div className="admin-page">
        <div className="loading">Loading features...</div>
      </div>
    );
  }

  // Prevent access to main admin panel if setup is not complete
  // BUT allow access to password change and 2FA setup screens
  if (isAuthenticated && !setupComplete && !showPasswordChange && !show2FASetup) {
    return (
      <div className="admin-page">
        <div className="setup-required">
          <h2>Setup Required</h2>
          <p>Please complete the security setup process to continue.</p>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-header">
        <h1>Feature Manager - Admin</h1>
        <div className="header-actions">
          <span className="user-info">
            Welcome, {user?.username} {user?.twoFactorEnabled && '🔒'}
          </span>
          <button onClick={createBackup} className="backup-btn">💾 Create Backup</button>
          <button onClick={totalReset} className="reset-btn">🗑️ Total Reset</button>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
          <Link to="/" className="back-link">← Back to User Page</Link>
        </div>
      </header>

      {backupStatus && (
        <div className="backup-status">
          {backupStatus}
        </div>
      )}

      <div className="admin-tabs">
        <button
          className={`admin-tab-btn ${activeTab === 'features' ? 'active' : ''}`}
          onClick={() => setActiveTab('features')}
        >
          Features ({Object.values(features).reduce((sum, categoryFeatures) => sum + categoryFeatures.length, 0)})
        </button>
        <button
          className={`admin-tab-btn ${activeTab === 'submissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('submissions')}
        >
          Submissions ({submissions.length})
        </button>
        <button
          className={`admin-tab-btn ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
        >
          Security
        </button>
        <button
          className={`admin-tab-btn ${activeTab === 'backups' ? 'active' : ''}`}
          onClick={() => setActiveTab('backups')}
        >
          Backups
        </button>
      </div>

      <main className="admin-content">
        {/* Features Tab */}
        {activeTab === 'features' && (
          <>
            {Object.keys(features).length === 0 ? (
              <div className="empty-state">
                <p>No features have been submitted yet.</p>
              </div>
            ) : (
              <>
                <div className="admin-summary">
                  <div className="summary-card">
                    <h3>Total Categories</h3>
                    <span className="summary-number">{Object.keys(features).length}</span>
                  </div>
                  <div className="summary-card">
                    <h3>Total Features</h3>
                    <span className="summary-number">
                      {Object.values(features).reduce((sum, categoryFeatures) => sum + categoryFeatures.length, 0)}
                    </span>
                  </div>
                  <div className="summary-card">
                    <h3>Total Votes</h3>
                    <span className="summary-number">
                      {Object.values(features).reduce((sum, categoryFeatures) => 
                        sum + categoryFeatures.reduce((catSum, feature) => catSum + (feature.votes || 0), 0), 0
                      )}
                    </span>
                  </div>
                </div>
                
                <div className="reorganize-section">
                  <button
                    onClick={triggerReorganization}
                    disabled={isReorganizing}
                    className="reorganize-btn"
                  >
                    {isReorganizing ? 'AI Agent Reorganizing...' : '🤖 Trigger AI Reorganization'}
                  </button>
                  {reorganizeMessage && (
                    <div className={`reorganize-message ${reorganizeMessage.includes('Error') ? 'error' : 'success'}`}>
                      {reorganizeMessage}
                    </div>
                  )}
                </div>
                
                <div className="systems-container">
                  {Object.entries(features).map(([system, systemFeatures]) => (
                    <div key={system} className="system-section">
                      <h2 className="system-title">
                        <span>
                          {system} System
                          <span className="system-count">({systemFeatures.length} features)</span>
                        </span>
                      </h2>
                      <div className="features-hierarchy">
                        {renderFeatureHierarchy(systemFeatures)}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <>
            {submissions.length === 0 ? (
              <div className="empty-state">
                <p>No user submissions yet.</p>
              </div>
            ) : (
              <div className="submissions-container">
                <h2>User Submissions</h2>
                <div className="submissions-list">
                  {submissions.map(submission => (
                    <div key={submission.id} className="submission-item">
                      <div className="submission-content">
                        <p className="submission-text">"{submission.original_text}"</p>
                        <small className="submission-date">
                          {new Date(submission.created_at).toLocaleDateString()} at {new Date(submission.created_at).toLocaleTimeString()}
                        </small>
                      </div>
                      <button
                        onClick={() => deleteSubmission(submission.id)}
                        className="delete-btn"
                        title="Delete submission"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="security-container">
            <h2>Security Settings</h2>
            
            <div className="security-section">
              <h3>Two-Factor Authentication</h3>
              {user?.twoFactorEnabled ? (
                <div className="security-status enabled">
                  <span>✅ 2FA is enabled</span>
                  <p>Your account is protected with two-factor authentication.</p>
                </div>
              ) : (
                <div className="security-status disabled">
                  <span>❌ 2FA is not enabled</span>
                  <p>Enable two-factor authentication for enhanced security.</p>
                  <button onClick={setup2FA} className="enable-2fa-btn">Enable 2FA</button>
                </div>
              )}
            </div>

            <div className="security-section">
              <h3>Password Management</h3>
              <p>Last password change: {user?.passwordChangedAt || 'Never'}</p>
              <button onClick={() => setShowPasswordChange(true)} className="change-password-btn">
                Change Password
              </button>
            </div>

            <div className="security-section">
              <h3>Account Information</h3>
              <p><strong>Username:</strong> {user?.username}</p>
              <p><strong>Email:</strong> {user?.email || 'Not set'}</p>
              <p><strong>Account Created:</strong> {user?.createdAt || 'Unknown'}</p>
              <p><strong>Last Login:</strong> {user?.lastLogin || 'Never'}</p>
            </div>
          </div>
        )}

        {/* Backups Tab */}
        {activeTab === 'backups' && (
          <div className="backups-container">
            <h2>Backup Management</h2>
            
            <div className="backup-actions">
              <button onClick={createBackup} className="backup-btn">💾 Create New Backup</button>
              <button onClick={loadBackups} className="refresh-btn" disabled={isLoadingBackups}>
                {isLoadingBackups ? '🔄 Loading...' : '🔄 Refresh Backups'}
              </button>
            </div>

            <div className="import-section">
              <h3>Import External Backup</h3>
              <p>Upload CSV backup files to restore data from an external source.</p>
              
              <div className="file-upload-container">
                <div className="file-input-group">
                  <label htmlFor="featuresFile">Features CSV:</label>
                  <input
                    type="file"
                    id="featuresFile"
                    accept=".csv"
                    onChange={(e) => setFeaturesFile(e.target.files[0])}
                  />
                </div>
                <div className="file-input-group">
                  <label htmlFor="submissionsFile">Submissions CSV:</label>
                  <input
                    type="file"
                    id="submissionsFile"
                    accept=".csv"
                    onChange={(e) => setSubmissionsFile(e.target.files[0])}
                  />
                </div>
                <button
                  onClick={() => importBackupFiles(featuresFile, submissionsFile)}
                  className="import-btn"
                  disabled={!featuresFile || !submissionsFile}
                >
                  📥 Import Backup
                </button>
              </div>
            </div>

            {backupStatus && (
              <div className="backup-status">
                {backupStatus}
              </div>
            )}

            {restoreStatus && (
              <div className="restore-status">
                {restoreStatus}
              </div>
            )}

            {importStatus && (
              <div className="import-status">
                {importStatus}
              </div>
            )}

            <div className="backups-list">
              <h3>Available Backups</h3>
              {isLoadingBackups ? (
                <div className="loading-state">
                  <p>Loading backups...</p>
                </div>
              ) : backups.length === 0 ? (
                <div className="empty-state">
                  <p>No backups available. Create your first backup to get started.</p>
                </div>
              ) : (
                <div className="backup-items">
                  {backups.map((backup) => (
                    <div key={backup.timestamp} className="backup-item">
                      <div className="backup-info">
                        <div className="backup-date">
                          <strong>{backup.readableDate}</strong>
                          <div className="backup-label">
                            {editingBackup === backup.timestamp ? (
                              <div className="label-edit">
                                <input
                                  type="text"
                                  value={editLabel}
                                  onChange={(e) => setEditLabel(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveLabel(backup.timestamp);
                                    if (e.key === 'Escape') cancelEditingLabel();
                                  }}
                                  placeholder="Enter label..."
                                  autoFocus
                                />
                                <button onClick={() => saveLabel(backup.timestamp)} className="save-label-btn" title="Save">✓</button>
                                <button onClick={cancelEditingLabel} className="cancel-label-btn" title="Cancel">✗</button>
                              </div>
                            ) : (
                              <div className="label-display">
                                <span className="label-text">
                                  {backup.label || (backup.type === 'auto' ? '(Auto Backup)' : '(No label)')}
                                </span>
                                <button onClick={() => startEditingLabel(backup)} className="edit-label-btn" title="Edit label">✏️</button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="backup-files">
                          <span className="file-info">
                            📊 Features: {backup.features}
                            {backup.hasFeatures ? ' ✅' : ' ⚠️ (Empty)'}
                          </span>
                          <span className="file-info">
                            📝 Submissions: {backup.submissions}
                            {backup.hasSubmissions ? ' ✅' : ' ⚠️ (Empty)'}
                          </span>
                          {backup.hasComments && (
                            <span className="file-info">
                              💬 Comments: {backup.comments} ✅
                            </span>
                          )}
                        </div>
                        <div className="backup-timestamp">
                          <small>ID: {backup.timestamp}</small>
                        </div>
                      </div>
                      <div className="backup-actions">
                        <button
                          onClick={() => restoreFromBackup(backup.timestamp)}
                          className="restore-btn"
                          title="Restore from this backup"
                        >
                          🔄 Restore
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="backup-info-section">
              <h3>About Backups</h3>
              <div className="info-grid">
                <div className="info-item">
                  <h4>📅 Frequency</h4>
                  <p>Automated backups are created every hour. Manual backups can be created at any time.</p>
                </div>
                <div className="info-item">
                  <h4>💾 Storage</h4>
                  <p>Backups are stored as CSV files in the server's backup directory. Old backups are automatically cleaned up after 7 days.</p>
                </div>
                <div className="info-item">
                  <h4>⚠️ Restore Warning</h4>
                  <p>Restoring from a backup will completely overwrite all current data. This action cannot be undone.</p>
                </div>
                <div className="info-item">
                  <h4>🔒 Security</h4>
                  <p>Only administrators can access backup and restore functionality. All operations are logged.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminPage;
